import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Plus, Trash2, Save, X, Bell, AlertTriangle, Info, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner, Card, PrimaryButton, SecondaryButton, Input, Textarea, Select, EmptyState } from "./_ui";

const SEVERITY_META = {
  info: { icon: Info, color: "#60A5FA", bg: "rgba(59, 130, 246, 0.12)", label: "Information" },
  success: { icon: CheckCircle2, color: "#34D399", bg: "rgba(16, 185, 129, 0.12)", label: "Succès" },
  warning: { icon: AlertTriangle, color: "#FBBF24", bg: "rgba(245, 158, 11, 0.12)", label: "Alerte" },
};

const EMPTY = { title: "", body: "", severity: "info" };

export default function NotificationsPanel() {
  const [items, setItems] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/notifications").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title || !form.body) return toast.error("Titre et message requis");
    setBusy(true);
    try {
      await api.post("/admin/notifications", form);
      toast.success("Notification publiée");
      setAdding(false); setForm(EMPTY); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette notification ?")) return;
    await api.delete(`/admin/notifications/${id}`);
    toast.success("Supprimée"); await load();
  };

  if (!items) return (<><PageHeader title="Notifications" /><Spinner /></>);

  return (
    <div data-testid="admin-notifications">
      <PageHeader
        title="Notifications"
        subtitle="Diffusez des alertes internes aux administrateurs et coordinateurs."
        action={
          !adding && (
            <PrimaryButton onClick={() => setAdding(true)} data-testid="notif-create-btn">
              <Plus size={16} /> Nouvelle notification
            </PrimaryButton>
          )
        }
      />

      {adding && (
        <Card className="p-5 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input placeholder="Titre" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="md:col-span-2" data-testid="notif-title" />
            <Select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })}>
              <option value="info">Information</option>
              <option value="success">Succès</option>
              <option value="warning">Alerte</option>
            </Select>
          </div>
          <Textarea placeholder="Message" rows={3} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="mt-3" data-testid="notif-body" />
          <div className="flex gap-3 mt-4">
            <PrimaryButton onClick={save} disabled={busy} data-testid="notif-save"><Save size={16} /> Publier</PrimaryButton>
            <SecondaryButton onClick={() => { setAdding(false); setForm(EMPTY); }}><X size={16} /> Annuler</SecondaryButton>
          </div>
        </Card>
      )}

      {items.length === 0 ? (
        <EmptyState icon={Bell} title="Aucune notification" hint="Diffusez la première alerte au staff." />
      ) : (
        <div className="space-y-3">
          {items.map((n) => {
            const meta = SEVERITY_META[n.severity] || SEVERITY_META.info;
            const Icon = meta.icon;
            return (
              <Card key={n.id} className="p-5 flex gap-4" data-testid={`notif-${n.id}`}>
                <div className="w-10 h-10 rounded-lg grid place-items-center shrink-0"
                  style={{ background: meta.bg, color: meta.color }}>
                  <Icon size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="text-sm font-medium text-white">{n.title}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{meta.label} · {n.author} · {new Date(n.created_at).toLocaleDateString("fr-FR")}</div>
                    </div>
                    <button onClick={() => remove(n.id)} className="text-slate-500 hover:text-[#CE1126]"><Trash2 size={14} /></button>
                  </div>
                  <p className="text-sm text-slate-300 mt-2">{n.body}</p>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
